import type { ReactNode } from 'react';
import { resolveActionIcon, resolveActionIdFromLabel } from '@/shared/lib/actionIcons';
import { Button, type ButtonVariant } from '@/shared/ui/core/Button';
import {
  Modal,
  type ModalHeaderTone,
  type ModalHeight,
  type ModalIconTone,
  type ModalIconVariant,
  type ModalSize,
} from '@/shared/ui/core/Modal';
import type { ControlSize } from '@/shared/ui/core/types';

export type ContentModalAction = {
  id?: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  /** Override icon lookup (`id` → label heuristic). */
  iconKey?: string;
};

export type ContentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Optional icon in front of title (kích thước = icon X: size-4). */
  icon?: ReactNode;
  /** Presentation style: 'badge' (soft-tinted rounded box matching close X button) or 'inline' (naked glyph). Default 'badge'. */
  iconVariant?: ModalIconVariant;
  /** Color theme for icon badge/glyph. Default 'primary'. */
  iconTone?: ModalIconTone;
  /** Header bar color. Default stays the neutral surface. */
  headerTone?: ModalHeaderTone;
  /** Custom classes for title icon. */
  iconClassName?: string;
  /** Short entity key / doc id only — not training copy. */
  description?: string;
  children: ReactNode;
  size?: ModalSize;
  height?: ModalHeight;
  /**
   * When true (default), overlay click does not dismiss — close via Đóng / X.
   * Use for print preview, document viewer, focused review.
   */
  lockOutsideClose?: boolean;
  /** Show header X. Default true. */
  dismissible?: boolean;
  closeLabel?: string;
  /** Extra footer actions (left of primary). Rendered before Đóng. */
  actions?: ContentModalAction[];
  /** Rightmost primary action (e.g. In, Xác nhận). */
  primaryAction?: ContentModalAction;
  /** Hide the default Đóng button when custom footer is enough. */
  showCloseButton?: boolean;
  /** Footer button size. Default matches the shared modal (`lg`). */
  actionSize?: ControlSize;
  className?: string;
  bodyClassName?: string;
};

function actionLeftIcon(a: Pick<ContentModalAction, 'id' | 'label' | 'iconKey'>, fallbackId?: string) {
  const id = a.id ?? resolveActionIdFromLabel(a.label) ?? fallbackId;
  return id ? resolveActionIcon(id, a.iconKey, 'size-4') : null;
}

/**
 * Shared content / preview modal — print voucher, attachment HTML, report viewer.
 * Not for entity create/edit (`EntityFormModal`) or destructive confirm (`ConfirmationDialog`).
 */
export function ContentModal({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = 'badge',
  iconTone = 'primary',
  headerTone,
  iconClassName,
  description,
  children,
  size = '2xl',
  height = 'tall',
  lockOutsideClose = true,
  dismissible = true,
  closeLabel = 'Đóng',
  actions = [],
  primaryAction,
  showCloseButton = true,
  actionSize = 'md',
  className,
  bodyClassName,
}: ContentModalProps) {
  const footer = (
    <>
      {showCloseButton ? (
        <Button
          size={actionSize}
          variant="secondary"
          leftIcon={actionLeftIcon({ label: closeLabel }, 'dismiss')}
          onClick={() => onOpenChange(false)}
        >
          {closeLabel}
        </Button>
      ) : null}
      {actions.map((a) => (
        <Button
          key={a.id ?? a.label}
          size={actionSize}
          variant={a.variant ?? 'secondary'}
          disabled={a.disabled}
          loading={a.loading}
          leftIcon={actionLeftIcon(a)}
          onClick={a.onClick}
        >
          {a.label}
        </Button>
      ))}
      {primaryAction ? (
        <Button
          size={actionSize}
          variant={primaryAction.variant ?? 'primary'}
          disabled={primaryAction.disabled}
          loading={primaryAction.loading}
          leftIcon={actionLeftIcon(primaryAction)}
          onClick={primaryAction.onClick}
        >
          {primaryAction.label}
        </Button>
      ) : null}
    </>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      icon={icon}
      iconVariant={iconVariant}
      iconTone={iconTone}
      headerTone={headerTone}
      iconClassName={iconClassName}
      description={description}
      size={size}
      height={height}
      dismissible={dismissible}
      preventOutsideClose={lockOutsideClose}
      className={className}
      bodyClassName={bodyClassName}
      footer={footer}
    >
      {children}
    </Modal>
  );
}
